/**
 * @fileoverview Main Operator Administration Panel for RunToGoal (Tiro a Gol V2).
 * Allows stadium operators to manage visual themes, dynamic N zones, match physics,
 * real-time telemetry from SignalR/WebSockets, and project cover and interactive game canvas.
 * 
 * @module components/Admin/Games/RunToGoalV3
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { SecondaryBase } from './../Commons/SecondaryBase';
import { useFloatMessage, MESSAGE_TYPE } from '../../Providers/FloatMessage';
import { Confirm } from '../../Commons/Modals/Confirm';
import { RunToGoalThemeModal } from './RunToGoal/RunToGoalThemeModal';

import { 
    GetThemes,
    CloneTheme,
    DeleteTheme,
    AssignThemeToRoom,
    SeedPresets,
    CreateRunToGoalV2Match, 
    StartRunToGoalV2Match, 
    ResetRunToGoalV2Match, 
    FinishRunToGoalV2MatchEarly, 
    ShowRunToGoalV2FrontPage 
} from './../../../utilities/BL/RunToGoal';

import { ConnectWebSocketForGoalAdmin, DisconnectWebSocketForGoalAdmin } from './../../../utilities/WS/Admin/RunToGoalWS';
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones';
import { SetScreenV2MainDynamic } from './../../../utilities/BL/ScreenV2';

/**
 * Panel de Administración y Control del Operador para Tiro a Gol V2 (RunToGoal).
 * 
 * @function RunToGoalV3
 * @param {Object} props - Propiedades del componente.
 * @param {Function} props.getOutOfTheDynamic - Callback para salir de la dinámica al Dashboard.
 * @param {string} [props.roomId] - Identificador GUID de la sala activa.
 * @returns {React.ReactElement} Panel de control de operador de Tiro a Gol.
 */
const RunToGoalV3 = ({ getOutOfTheDynamic, roomId: propRoomId }) => {
    const { showMessage } = useFloatMessage();

    // Detección segura de Sala SaaS
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const activeRoomId = propRoomId ||
        searchParams.get('roomId') ||
        (typeof localStorage !== 'undefined'
            ? (localStorage.getItem('active_room_id') || localStorage.getItem('roomId') || localStorage.getItem('selected_room_id') || localStorage.getItem('RoomId'))
            : '') ||
        '';

    // ==========================================================================
    // ESTADOS DE TEMAS VISUALES Y MODALES
    // ==========================================================================
    const [availableThemesList, setAvailableThemesList] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(0);
    const [activeThemeObject, setActiveThemeObject] = useState(null);
    const activeThemeObjectRef = useRef(activeThemeObject);

    useEffect(() => {
        activeThemeObjectRef.current = activeThemeObject;
    }, [activeThemeObject]);

    const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);
    const [themeToEditObject, setThemeToEditObject] = useState(null);

    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [confirmModalConfiguration, setConfirmModalConfiguration] = useState({
        message: '',
        yesButtonText: 'Sí, continuar',
        noButtonText: 'Cancelar',
        actionToExecute: () => {}
    });

    // ==========================================================================
    // ESTADOS OPERATIVOS DE PARTIDA
    // ==========================================================================
    const [isSystemWorking, setIsSystemWorking] = useState(false);
    const [hasRoomBeenOpened, setHasRoomBeenOpened] = useState(false);
    const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);

    const [matchDurationSeconds, setMatchDurationSeconds] = useState(30);
    const [tapsPerTick, setTapsPerTick] = useState(10);
    const [minimumUpdateMilliseconds, setMinimumUpdateMilliseconds] = useState(100);

    const [currentMatchId, setCurrentMatchId] = useState(null);
    const [matchPhase, setMatchPhase] = useState('WAITING');
    const [websocketStatus, setWebsocketStatus] = useState('idle');

    const [liveStadiumStats, setLiveStadiumStats] = useState(null);
    const [liveZonesProgress, setLiveZonesProgress] = useState([]);
    const [currentElapsedSeconds, setCurrentElapsedSeconds] = useState(0);
    const [matchWinnerData, setMatchWinnerData] = useState(null);

    // ==========================================================================
    // CARGA DE TEMAS Y ASIGNACIÓN DE SALA
    // ==========================================================================
    const loadThemesList = useCallback(async (preferredThemeId = null) => {
        try {
            const themesResponse = await GetThemes(activeRoomId);
            if (themesResponse && themesResponse.success && Array.isArray(themesResponse.data)) {
                const themes = themesResponse.data;
                setAvailableThemesList(themes);

                let themeToSelect = null;
                if (preferredThemeId && preferredThemeId > 0) {
                    themeToSelect = themes.find(t => (t.id || t.Id) === Number(preferredThemeId));
                }

                if (!themeToSelect && themes.length > 0) {
                    themeToSelect = themes.find(t => t.isSystemPreset || t.IsSystemPreset) || themes[0];
                }

                if (themeToSelect) {
                    const themeId = themeToSelect.id || themeToSelect.Id;
                    setSelectedThemeId(themeId);
                    setActiveThemeObject(themeToSelect);
                }
            }
        } catch (error) {
            console.error('[RunToGoalV3] Error al cargar lista de temas:', error);
        }
    }, [activeRoomId]);

    useEffect(() => {
        loadThemesList();
    }, [loadThemesList]);

    const handleActiveThemeSelectionChange = async (event) => {
        const newThemeId = Number(event.target.value) || 0;
        setSelectedThemeId(newThemeId);

        const foundTheme = availableThemesList.find(t => (t.id || t.Id) === newThemeId);
        setActiveThemeObject(foundTheme || null);

        if (newThemeId > 0 && activeRoomId) {
            try {
                await AssignThemeToRoom(newThemeId, activeRoomId);
            } catch (err) {
                console.warn('[RunToGoalV3] Error al asignar tema a la sala:', err);
            }
        }
    };

    // ==========================================================================
    // ACCIONES CRUD DE TEMAS
    // ==========================================================================
    const handleOpenCreateThemeModal = () => {
        setThemeToEditObject(null);
        setIsThemeModalOpen(true);
    };

    const handleOpenEditThemeModal = () => {
        const foundTheme = availableThemesList.find((item) => (item.id || item.Id) === selectedThemeId);
        setThemeToEditObject(foundTheme || null);
        setIsThemeModalOpen(true);
    };

    const handleCloneSelectedTheme = async () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Clonando tema...' });
        try {
            const cloneResponse = await CloneTheme(selectedThemeId);
            if (cloneResponse && cloneResponse.success && cloneResponse.data) {
                const newId = cloneResponse.data.id || cloneResponse.data.Id;
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema clonado exitosamente.' });
                await loadThemesList(newId);
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: cloneResponse?.message || 'Error al clonar tema.' });
            }
        } catch (cloneError) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al clonar tema.' });
        }
    };

    const handleConfirmDeleteTheme = () => {
        if (!selectedThemeId || selectedThemeId <= 0) return;
        const currentTheme = availableThemesList.find((t) => (t.id || t.Id) === selectedThemeId);
        
        if (currentTheme?.isSystemPreset || currentTheme?.IsSystemPreset) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Los temas base del sistema no pueden ser eliminados.' });
            return;
        }

        const themeName = currentTheme ? currentTheme.name : 'este tema';

        setConfirmModalConfiguration({
            message: `¿Estás seguro de que deseas eliminar permanentemente el tema "${themeName}"?`,
            yesButtonText: 'Sí, eliminar',
            noButtonText: 'Cancelar',
            actionToExecute: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Eliminando tema...' });
                const deleteResponse = await DeleteTheme(selectedThemeId);
                if (deleteResponse && deleteResponse.success) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Tema eliminado correctamente.' });
                    await loadThemesList();
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: deleteResponse?.message || 'Error al eliminar el tema.' });
                }
                setIsConfirmModalOpen(false);
                return { success: true };
            }
        });
        setIsConfirmModalOpen(true);
    };

    const handleSeedSystemPresets = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Verificando y cargando presets del sistema...' });
        try {
            const seedResponse = await SeedPresets();
            if (seedResponse && seedResponse.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Presets del sistema sincronizados correctamente.' });
                await loadThemesList();
            } else {
                showMessage({ type: MESSAGE_TYPE.BAD, text: seedResponse?.message || 'Error al cargar presets.' });
            }
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error inesperado al cargar presets del sistema.' });
        }
    };

    // ==========================================================================
    // PROCESADOR WEBSOCKET
    // ==========================================================================
    const handleIncomingWebsocketMessage = useCallback((incomingMessage) => {
        const messageType = (incomingMessage?.type || '').toLowerCase();
        const payloadData = incomingMessage?.payload || {};

        switch (messageType) {
            case 'goal.created':
            case 'goal.reset':
                setCurrentMatchId(payloadData.matchId);
                setMatchPhase('WAITING');
                setLiveZonesProgress([]);
                setCurrentElapsedSeconds(0);
                setMatchWinnerData(null);
                break;

            case 'goal.started':
                setMatchPhase('RUNNING');
                setLiveZonesProgress([]);
                setCurrentElapsedSeconds(0);
                setMatchWinnerData(null);
                break;

            case 'goal.progress':
                if (payloadData.phase && payloadData.phase.toUpperCase() === 'RUNNING') {
                    setMatchPhase('RUNNING');
                }
                if (payloadData.zonesProgress) {
                    setLiveZonesProgress(payloadData.zonesProgress);
                }
                if (payloadData.elapsedMilliseconds !== undefined) {
                    setCurrentElapsedSeconds(Math.floor(payloadData.elapsedMilliseconds / 1000));
                }
                break;

            case 'goal.finished':
                setMatchPhase('FINISHED');
                if (payloadData.finalZonesProgress) {
                    setLiveZonesProgress(payloadData.finalZonesProgress);

                    const winnerString = payloadData.winner;
                    if (winnerString === 'tie') {
                        setMatchWinnerData({ text: "¡EMPATE TOTAL!", points: payloadData.finalZonesProgress[0]?.taps || 0 });
                    } else if (winnerString.startsWith('tie_partial:')) {
                        const tiedZonesList = winnerString.split(':')[1];
                        setMatchWinnerData({ text: `¡EMPATE PARCIAL! (${tiedZonesList})`, points: payloadData.finalZonesProgress.find(z => (z.zone || z.zoneIdentifier) === tiedZonesList.split(' y ')[0].trim())?.taps || 0 });
                    } else {
                        const winnerZone = payloadData.finalZonesProgress.find(z => (z.zone || z.zoneIdentifier) === winnerString);
                        setMatchWinnerData({ text: `ZONA ${winnerString.toUpperCase()}`, points: winnerZone?.taps || winnerZone?.totalTaps || 0 });
                    }
                }
                break;

            default:
                break;
        }
    }, []);

    useEffect(() => {
        ConnectWebSocketForGoalAdmin(handleIncomingWebsocketMessage, setWebsocketStatus);
        return () => { DisconnectWebSocketForGoalAdmin(); };
    }, [handleIncomingWebsocketMessage]);

    useEffect(() => {
        const fetchStadiumLiveStats = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}Stadium/LiveStats`);
                if (response.ok) setLiveStadiumStats(await response.json());
            } catch (error) { }
        };
        fetchStadiumLiveStats();
        const pollingInterval = setInterval(fetchStadiumLiveStats, 3000);
        return () => clearInterval(pollingInterval);
    }, []);

    // ==========================================================================
    // ACCIONES DE BOTONES TÁCTICOS
    // ==========================================================================
    
    // PASO 1: ABRIR SALA Y MOSTRAR QR
    const handleOpenRoomAndShowQR = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Limpiando estadio y mostrando QR..." });

            await BLResetCurrentDynamic(); 
            
            // 1. Mandamos la señal de Welcome (QR)
            await fetch(`${process.env.REACT_APP_API_URL}Admin/sendWelcome`, {
                method: 'POST'
            });

            // 2. Creamos la ronda en BD y purgamos los stats viejos
            const themeName = activeThemeObject?.name || 'Clásico';
            const fullRoundName = `Tiro a Gol - ${themeName}`;
            await fetch(`${process.env.REACT_APP_API_URL}Stadium/StartNewRound`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ roundName: fullRoundName })
            });

            setHasRoomBeenOpened(true);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Sala abierta con éxito. QR en pantalla." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al purgar o al enviar el QR." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // PASO 2: MOSTRAR PORTADA DEL JUEGO
    const handleShowFrontPage = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Enviando portada a pantalla..." });

            await ShowRunToGoalV2FrontPage(activeRoomId, selectedThemeId ? Number(selectedThemeId) : null);

            // MAGIA V2: Le decimos al Súper Lienzo que inyecte el componente RunToGoal
            await SetScreenV2MainDynamic(1, 'RunToGoal');

            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Portada del juego en pantalla." });
        } catch (error) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al mostrar la portada." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    // PASO 3: INICIAR JUEGO
    const handleStartGame = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: `Preparando carrera...` });

            const creationResponse = await CreateRunToGoalV2Match({
                matchDurationSeconds: matchDurationSeconds,
                tapsPerTick: tapsPerTick,
                minimumUpdateMilliseconds: minimumUpdateMilliseconds,
                themeId: selectedThemeId ? Number(selectedThemeId) : null,
                roomId: activeRoomId || null
            });

            if (!creationResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: creationResponse.message });
                return;
            }

            const newMatchId = creationResponse.data?.matchIdentifier;
            setCurrentMatchId(newMatchId);

            const startResponse = await StartRunToGoalV2Match(newMatchId);

            if (!startResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: startResponse.message });
                await ResetRunToGoalV2Match(newMatchId);
            } else {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `¡Carrera en curso!` });
            }

        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Ocurrió un error inesperado al iniciar." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleFinishEarly = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Terminando partida..." });
            if (!currentMatchId) return;

            const finishResponse = await FinishRunToGoalV2MatchEarly(currentMatchId);
            if (!finishResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: finishResponse.message });
            }
        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al terminar la partida anticipadamente." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleAbortGame = async () => {
        try {
            setIsSystemWorking(true);
            showMessage({ type: MESSAGE_TYPE.INFO, text: "Abortando partida..." });
            if (!currentMatchId) return;

            const resetResponse = await ResetRunToGoalV2Match(currentMatchId);
            if (!resetResponse.success) {
                showMessage({ type: MESSAGE_TYPE.BAD, text: resetResponse.message });
            } else {
                // MAGIA V2: Limpiamos el Súper Lienzo
                await SetScreenV2MainDynamic(1, null);

                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Partida abortada." });
            }
        } catch (exception) {
            showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al abortar la partida." });
        } finally {
            setIsSystemWorking(false);
        }
    };

    const handleReturnToDashboard = async () => {
        try {
            // MAGIA V2: Limpiamos el Súper Lienzo al salirnos de la sección
            await SetScreenV2MainDynamic(1, null);
            
            await BLResetCurrentDynamic();
            getOutOfTheDynamic();
        } catch (error) { }
    };

    const formatTime = (totalSeconds) => {
        const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
        const seconds = (totalSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    };

    const getVisualZoneName = (realZoneName) => {
        if (!realZoneName) return '';
        const normalized = realZoneName.toLowerCase().trim();

        // Buscar en las zonas del tema activo
        const currentTheme = activeThemeObjectRef.current;
        if (currentTheme) {
            let zonesList = currentTheme.zones || currentTheme.Zones;
            if (!zonesList && (currentTheme.zonesConfigJson || currentTheme.ZonesConfigJson)) {
                try {
                    zonesList = JSON.parse(currentTheme.zonesConfigJson || currentTheme.ZonesConfigJson);
                } catch (e) {}
            }

            if (Array.isArray(zonesList)) {
                const matchedZone = zonesList.find(z => (z.zoneId || z.ZoneId || '').toLowerCase().trim() === normalized);
                if (matchedZone && (matchedZone.name || matchedZone.Name)) {
                    return matchedZone.name || matchedZone.Name;
                }
            }
        }

        const defaultAliases = {
            'zona1': 'Xolo Mayor Rojo',
            'zona2': 'Xolo Mayor Blanco',
            'zona3': 'Zona 3',
            'zona4': 'Zona 4'
        };

        return defaultAliases[normalized] || realZoneName;
    };

    const isMatchActive = matchPhase === 'RUNNING';

    // ==========================================================================
    // RENDERIZADO
    // ==========================================================================
    return (
        <SecondaryBase actionToExecute={handleReturnToDashboard}>
            {/* Modal de Confirmación */}
            {isConfirmModalOpen && (
                <Confirm
                    message={confirmModalConfiguration.message}
                    actionToYesButton={confirmModalConfiguration.actionToExecute}
                    textYesButton={confirmModalConfiguration.yesButtonText}
                    textNoButton={confirmModalConfiguration.noButtonText}
                    setShowModal={setIsConfirmModalOpen}
                />
            )}

            {/* Modal de Gestión de Temas */}
            {isThemeModalOpen && (
                <RunToGoalThemeModal
                    isOpen={isThemeModalOpen}
                    initialThemeData={themeToEditObject}
                    roomId={activeRoomId}
                    onCloseModal={() => setIsThemeModalOpen(false)}
                    onThemeSavedSuccessfully={(savedTheme) => {
                        const savedId = savedTheme?.id || savedTheme?.Id;
                        loadThemesList(savedId);
                    }}
                />
            )}

            <section className='AdmRunToGoal__Settings pb-5'>
                
                <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                    <p className='AdmRunToGoal__Title mb-0'>Tiro a Gol V2</p>
                    <span className="badge bg-light text-dark border small">
                        WebSocket: <strong className={websocketStatus === 'connected' ? 'text-success' : 'text-warning'}>{websocketStatus}</strong>
                    </span>
                </div>

                <div className='AdmRunToGoal__Settings__Controls'>
                    
                    <div className='AdmRunToGoal__Text--Center border-bottom pb-3 mb-4'>
                        <div className="d-flex justify-content-between align-items-center px-3">
                            <div>
                                <p className="mb-1 text-muted small">Estatus Servidor</p>
                                <p className="fw-bold mb-0">
                                    {matchPhase === 'WAITING' && <><i className="bi bi-hourglass-split"></i> En Espera</>}
                                    {matchPhase === 'RUNNING' && <><i className="bi bi-play-circle text-success"></i> ¡CARRERA EN CURSO!</>}
                                    {matchPhase === 'FINISHED' && <><i className="bi bi-stop-circle text-danger"></i> Terminada</>}
                                </p>
                            </div>

                            <div className="bg-dark text-white rounded px-4 py-2 shadow-sm text-center">
                                <p className="mb-0 small text-uppercase" style={{ letterSpacing: '1px', color: '#aaa' }}>Cronómetro</p>
                                <h3 className="mb-0 fw-bold" style={{ fontFamily: 'monospace' }}>
                                    {formatTime(currentElapsedSeconds)} / {formatTime(matchDurationSeconds)}
                                </h3>
                            </div>
                        </div>
                    </div>

                    {/* BOTONERA TÁCTICA */}
                    <div className="mt-4">
                        {matchPhase === 'WAITING' && (
                            <>
                                {/* GESTOR Y SELECTOR DE TEMAS */}
                                <div className="mb-4 bg-light p-3 rounded border shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="fw-bold text-dark small mb-0">
                                            <i className="bi bi-palette-fill text-primary me-1"></i> Tema Visual / Patrocinador:
                                        </label>
                                        <div className="d-flex gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary fw-bold py-1 px-2 text-nowrap"
                                                onClick={handleSeedSystemPresets}
                                                disabled={isSystemWorking || isMatchActive}
                                                title="Sincronizar / Restaurar Presets Base del Sistema"
                                            >
                                                <i className="bi bi-arrow-repeat me-1"></i> Cargar Presets
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-success fw-bold py-1 px-2 text-nowrap"
                                                onClick={handleOpenCreateThemeModal}
                                                disabled={isSystemWorking || isMatchActive}
                                                title="Crear nuevo tema visual"
                                            >
                                                <i className="bi bi-plus-lg me-1"></i> Nuevo Tema
                                            </button>
                                        </div>
                                    </div>

                                    <div className="d-flex align-items-center gap-2">
                                        <select 
                                            className="form-select fw-bold border-primary shadow-sm" 
                                            value={selectedThemeId || ''} 
                                            onChange={handleActiveThemeSelectionChange}
                                            disabled={isSystemWorking || isMatchActive}
                                        >
                                            <option value="">-- Seleccionar Tema --</option>
                                            {availableThemesList.map(themeItem => (
                                                <option key={themeItem.id || themeItem.Id} value={themeItem.id || themeItem.Id}>
                                                    {themeItem.name || themeItem.Name} {themeItem.isSystemPreset || themeItem.IsSystemPreset ? '⭐ (Sistema)' : ''}
                                                </option>
                                            ))}
                                        </select>

                                        <button
                                            type="button"
                                            className="btn btn-outline-primary fw-bold p-2 text-nowrap"
                                            onClick={handleOpenEditThemeModal}
                                            disabled={!selectedThemeId || isSystemWorking || isMatchActive}
                                            title="Editar tema seleccionado"
                                        >
                                            <i className="bi bi-pencil-square"></i>
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary fw-bold p-2 text-nowrap"
                                            onClick={handleCloneSelectedTheme}
                                            disabled={!selectedThemeId || isSystemWorking || isMatchActive}
                                            title="Duplicar / Clonar tema"
                                        >
                                            <i className="bi bi-copy"></i>
                                        </button>

                                        <button
                                            type="button"
                                            className="btn btn-outline-danger fw-bold p-2 text-nowrap"
                                            onClick={handleConfirmDeleteTheme}
                                            disabled={!selectedThemeId || activeThemeObject?.isSystemPreset || activeThemeObject?.IsSystemPreset || isSystemWorking || isMatchActive}
                                            title="Eliminar tema personalizado"
                                        >
                                            <i className="bi bi-trash3"></i>
                                        </button>
                                    </div>
                                    
                                    {activeThemeObject && (
                                        <div className="mt-2 pt-2 border-top d-flex justify-content-between align-items-center text-muted small">
                                            <span>Modo: <strong className="text-dark text-capitalize">{activeThemeObject.gameModeType || activeThemeObject.GameModeType || 'soccer'}</strong></span>
                                            <span>Zonas: <strong className="text-dark">{activeThemeObject.zones?.length || activeThemeObject.Zones?.length || 2}</strong></span>
                                            <span>Balón: <strong className="text-dark">{activeThemeObject.hasBall ?? activeThemeObject.HasBall ? 'Sí' : 'No'}</strong></span>
                                        </div>
                                    )}
                                </div>

                                {/* FASE 1 */}
                                <div className="row g-2 mb-4 pb-4 border-bottom">
                                    <div className="col-12 mb-2">
                                        <p className="fw-bold text-muted small mb-1">FASE 1: PREPARACIÓN</p>
                                    </div>
                                    <div className="col-sm-6">
                                        <button
                                            className={`btn btn-danger fw-bold w-100 shadow-sm py-3 ${isSystemWorking ? 'disabled' : ''}`}
                                            onClick={handleOpenRoomAndShowQR}
                                            disabled={isSystemWorking}
                                        >
                                            <i className="bi bi-qr-code"></i> 1. Mostrar QR y abrir sala
                                        </button>
                                    </div>
                                    <div className="col-sm-6">
                                        <button
                                            className={`btn btn-info fw-bold w-100 shadow-sm py-3 ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`}
                                            onClick={handleShowFrontPage}
                                            disabled={!hasRoomBeenOpened || isSystemWorking}
                                        >
                                            <i className="bi bi-display"></i> 2. Mostrar portada
                                        </button>
                                    </div>
                                </div>

                                {/* FASE 2: OPERACIÓN Y TIEMPO */}
                                <div className="row g-2 mb-3">
                                    <div className="col-12 mb-2 d-flex justify-content-between align-items-center">
                                        <p className="fw-bold text-muted small mb-1">FASE 2: OPERACIÓN DE CARRERA</p>
                                        
                                        <div className="d-flex align-items-center">
                                            <label className="small fw-bold me-2 mb-0">Duración:</label>
                                            <div className="input-group input-group-sm" style={{ width: '110px' }}>
                                                <input
                                                    type="number"
                                                    className="form-control text-center fw-bold"
                                                    value={matchDurationSeconds}
                                                    onChange={e => setMatchDurationSeconds(+e.target.value || 10)}
                                                />
                                                <span className="input-group-text">seg</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* BOTÓN ÚNICO DE CARRERA */}
                                    <div className="col-12">
                                        <button
                                            className={`Btn__Adsl--RedOutlineTransparent AdmRunToGoal__Settings__Controls__Buttons w-100 m-0 py-3 ${(!hasRoomBeenOpened || isSystemWorking) ? 'disabled' : ''}`}
                                            onClick={handleStartGame}
                                            disabled={!hasRoomBeenOpened || isSystemWorking}
                                        >
                                            <i className="bi bi-play-circle-fill"></i> Iniciar carrera
                                        </button>
                                    </div>
                                </div>

                                {/* CONFIGURACIÓN AVANZADA (TAPS) */}
                                <div className="mt-3 text-end">
                                    <button 
                                        className="btn btn-sm btn-link text-muted text-decoration-none"
                                        onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
                                    >
                                        <i className={`bi bi-gear${showAdvancedConfig ? '-fill' : ''}`}></i> {showAdvancedConfig ? 'Ocultar' : 'Mostrar'} conf. avanzada
                                    </button>
                                    
                                    {showAdvancedConfig && (
                                        <div className="bg-light p-3 rounded border text-start mt-2 shadow-sm transition">
                                            <label className='small fw-bold mb-1'>Taps requeridos en celular (Batch):</label>
                                            <input
                                                className='form-control form-control-sm mb-2'
                                                style={{ maxWidth: '150px' }}
                                                type='number'
                                                min={1}
                                                value={tapsPerTick}
                                                onChange={e => setTapsPerTick(+e.target.value || 1)}
                                            />
                                            <small className="text-muted" style={{ fontSize: '0.75rem' }}>Cantidad de toques que acumula el dispositivo antes de mandarlos al servidor por websocket.</small>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {matchPhase === 'RUNNING' && (
                            <button
                                className={`btn btn-warning btn-lg fw-bold w-100 mb-3 shadow-sm ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleFinishEarly}
                                disabled={isSystemWorking}
                            >
                                <i className="bi bi-trophy-fill"></i> Terminar antes (Declarar ganador)
                            </button>
                        )}

                        {(matchPhase === 'RUNNING' || matchPhase === 'FINISHED') && (
                            <button
                                className={`btn btn-outline-danger fw-bold w-100 ${isSystemWorking ? 'disabled' : ''}`}
                                onClick={handleAbortGame}
                                disabled={!currentMatchId || isSystemWorking}
                            >
                                <i className="bi bi-x-octagon-fill"></i> {matchPhase === 'FINISHED' ? 'Limpiar y volver a espera' : 'Abortar partida (Sin ganador)'}
                            </button>
                        )}
                    </div>
                </div>

                {matchPhase === 'FINISHED' && matchWinnerData && (
                    <div className="alert alert-success mt-4 p-4 text-center shadow-lg border-0" style={{ borderRadius: '15px' }}>
                        <h6 className="text-uppercase fw-bold mb-3" style={{ letterSpacing: '2px', color: '#198754' }}>Resultado del juego</h6>
                        <h2 className="fw-black mb-2">
                            {matchWinnerData.text.startsWith('ZONA') || matchWinnerData.text.startsWith('¡EMPATE PARCIAL!') ? (
                                getVisualZoneName(matchWinnerData.text.replace('ZONA ', '').replace('¡EMPATE PARCIAL! (', '').replace(')', ''))
                            ) : (
                                matchWinnerData.text
                            )}
                        </h2>
                        <h4 className="text-muted fw-normal">con <strong className="text-dark">{matchWinnerData.points}</strong> taps</h4>
                    </div>
                )}

                {/* DASHBOARD ANALÍTICO EN VIVO */}
                <div className='mt-5 mb-5 border-top pt-4'>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="fw-bold mb-0">Estadísticas en Vivo</h5>
                        {liveStadiumStats && (
                            <span className="badge bg-secondary">
                                {liveStadiumStats.roundName}
                            </span>
                        )}
                    </div>

                    {liveStadiumStats ? (
                        <>
                            <div className="row text-center mb-4 g-2">
                                <div className="col">
                                    <div className="p-3 bg-light rounded border">
                                        <h2 className="fw-black text-success mb-0">{liveStadiumStats.totalCurrentlyActive}</h2>
                                        <span className="small text-muted">JUGADORES AHORA</span>
                                    </div>
                                </div>
                                <div className="col">
                                    <div className="p-3 bg-light rounded border">
                                        <h2 className="fw-black text-secondary mb-0">{liveStadiumStats.totalEverConnected}</h2>
                                        <span className="small text-muted">TOTAL (Entraron)</span>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive mb-4">
                                <table className="table table-hover table-sm border align-middle text-center bg-white shadow-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-start ps-3">ZONA (Patrocinador)</th>
                                            <th>ACTIVOS</th>
                                            <th>ENTRARON</th>
                                            <th>SALIERON</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {liveStadiumStats.zoneStats.map((z, i) => (
                                            <tr key={i}>
                                                <td className="text-start ps-3 fw-bold text-uppercase">{getVisualZoneName(z.zone)}</td>
                                                <td className="fs-6 text-success fw-bold">{z.currentActive}</td>
                                                <td>{z.totalEntered}</td>
                                                <td className="text-danger">{z.totalLeft}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    ) : (
                        <div className="text-center p-4 text-muted">
                            <div className="spinner-border mb-3" role="status"></div>
                            <p>Cargando datos del estadio...</p>
                        </div>
                    )}

                    {/* Tabla de Taps */}
                    {liveZonesProgress.length > 0 && (
                        <div className="mt-4">
                            <h6 className="fw-bold text-muted text-uppercase mb-3"><i className="bi bi-activity text-primary"></i> Avance de Taps en Carrera</h6>
                            {liveZonesProgress.map((zoneData, index) => (
                                <div key={index} className="mb-2 bg-white p-3 rounded shadow-sm border border-start-0 border-end-0 border-4 border-primary">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <p className='fw-bold mb-0 text-uppercase'>
                                            {getVisualZoneName(zoneData.zoneIdentifier || zoneData.zone)}
                                        </p>
                                        <span className="badge bg-dark fs-5">{zoneData.totalTaps || zoneData.taps} taps</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </SecondaryBase>
    );
};

RunToGoalV3.propTypes = {
    getOutOfTheDynamic: PropTypes.func.isRequired,
    roomId: PropTypes.string
};

export { RunToGoalV3 };
export default RunToGoalV3;